import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Sun, Moon, Plus, Pencil, Trash2, AlertTriangle, FileSpreadsheet, Filter, X, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import {
  getColaboradoresMaoDeObra, postColaboradorMaoDeObra, getTabelaSalarialMaoDeObra,
  putColaboradorMaoDeObra, putReajusteMaoDeObra, putSemReajusteMaoDeObra, deleteColaboradorMaoDeObra,
} from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { KpiRowOrcamento, GaugeProgresso, TrendAccessory } from "../dashboard/KpiCard";
import {
  NIVEIS_CARGO, MODELOS_TRABALHO, REGIMES_MAO_DE_OBRA, AGRUPAMENTOS, ENQUADRAMENTOS,
  MESES_MAO_DE_OBRA, VISOES_MAO_DE_OBRA,
} from "../../constants/maoDeObra";
import { MESES_ABREV, formatCurrency, formatPercent } from "../../utils/orcamento";

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  danger: "#EF4444",
  navy: "#0F1B33",
  navyText: "#FFFFFF",
  lavanda: "#FBF3FC",
  rowAlt: isLight ? "#FAF9FD" : "#170F28",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);

function reajusteAtual(colaborador, ordem) {
  return colaborador[`reajuste_${ordem}`] || { ativo: false, agrupamento: null, enquadramento: null, regime: null, salario: null, salario_excecao: false, mes: null };
}

// Status Reajuste: calculado só no front, a partir do Reajuste 1 e/ou 2 já cadastrados —
// "Inserido" quando pelo menos um dos dois está marcado como ativo, ou quando o colaborador foi
// marcado como "sem reajuste no ano" (conta como preenchido no Progresso Orçamentário).
function reajustePreenchido(colaborador) {
  return !!(colaborador.sem_reajuste || reajusteAtual(colaborador, 1).ativo || reajusteAtual(colaborador, 2).ativo);
}

function custoTotalFolha(lista) {
  return lista.reduce((acc, c) => acc + Number(c.total_salario || 0) + Number(c.total_encargos || 0) + Number(c.total_beneficios || 0), 0);
}

// Cópia do colaborador usada como rascunho no modal de edição — reajuste_1/reajuste_2 vêm
// sempre normalizados (nunca undefined), para o diff de "o que mudou" na hora de salvar
// funcionar mesmo quando o colaborador nunca teve nenhum reajuste configurado.
function clonarColaborador(c) {
  return { ...c, sem_reajuste: !!c.sem_reajuste, reajuste_1: { ...reajusteAtual(c, 1) }, reajuste_2: { ...reajusteAtual(c, 2) } };
}

// Rascunho local (nunca gravado no backend) usado pelo modal ao clicar em "Nova contratação" —
// espelha exatamente os defaults que POST /orcamento/mao-de-obra aplicaria (ver
// routers/mao_de_obra.py:criar_colaborador), para que o diff feito em salvarEdicaoColaborador
// só capture os campos que o usuário de fato preencheu. Nada é criado no banco até o usuário
// alterar algo no modal e clicar em "Salvar alterações" (ver criarColaborador).
function novoColaboradorRascunho(departamento, ano) {
  return {
    id: null,
    departamento,
    cargo: "",
    nivel_cargo: "colaborador",
    plano_previdencia: false,
    modelo_trabalho: "presencial",
    agrupamento: 0,
    enquadramento: 1.00,
    regime_atual: "CLT",
    ocupante: null,
    salario_bruto_atual: 0,
    salario_excecao: false,
    vigencia_inicio: 1,
    vigencia_fim: 12,
    nova_contratacao: true,
    ano_referencia: ano,
    reajuste_1: { ordem: 1, ativo: false, agrupamento: null, enquadramento: null, regime: null, salario: null, salario_excecao: false, mes: null },
    reajuste_2: { ordem: 2, ativo: false, agrupamento: null, enquadramento: null, regime: null, salario: null, salario_excecao: false, mes: null },
    salario_bruto: 0,
    meses_salario: {}, meses_encargos: {}, meses_beneficios: {}, meses_custo_total: {},
    total_salario: 0, total_encargos: 0, total_beneficios: 0, total_custo_total: 0,
    composicao_encargos: [], composicao_beneficios: [],
  };
}

function rotuloOpcao(opcoes, valor) {
  if (valor === null || valor === undefined || valor === "") return "—";
  const opcao = opcoes.find((o) => o.value === valor);
  return opcao ? opcao.label : String(valor);
}
const rotuloAgrupamento = (valor) => (valor === null || valor === undefined ? "—" : String(valor));
const rotuloEnquadramento = (valor) => (valor === null || valor === undefined ? "—" : formatPercent(valor * 100));
const rotuloBooleano = (valor) => (valor ? "Sim" : "Não");
const rotuloMoeda = (valor) => (valor === null || valor === undefined ? "—" : formatCurrency(valor));

// Definição única das colunas de atributos da tabela (exclui meses/Total/Ações, que são
// calculados) — usada para gerar o cabeçalho com filtros, a exportação completa para Excel e
// a lista de valores únicos de cada filtro, evitando manter 3 listas de colunas em paralelo.
const COLUNAS_TABELA = [
  { key: "departamento", label: "Departamento", tituloExcel: "Departamento", align: "left", getValor: (c) => c.departamento },
  { key: "cargo", label: "Cargo/Função", tituloExcel: "Cargo/Função", align: "left", sticky: true, width: 190, getValor: (c) => c.cargo || "" },
  { key: "nivel_cargo", label: "Nível Cargo", tituloExcel: "Nível Cargo", getValor: (c) => c.nivel_cargo, getRotulo: (v) => rotuloOpcao(NIVEIS_CARGO, v) },
  { key: "plano_previdencia", label: "Previdência", tituloExcel: "Previdência", getValor: (c) => !!c.plano_previdencia, getRotulo: rotuloBooleano },
  { key: "modelo_trabalho", label: "Modelo Trabalho", tituloExcel: "Modelo Trabalho", getValor: (c) => c.modelo_trabalho, getRotulo: (v) => rotuloOpcao(MODELOS_TRABALHO, v) },
  { key: "agrupamento", label: "Agrupamento", tituloExcel: "Agrupamento", getValor: (c) => c.agrupamento, getRotulo: rotuloAgrupamento },
  { key: "enquadramento", label: "Enquadramento", tituloExcel: "Enquadramento", getValor: (c) => c.enquadramento, getRotulo: rotuloEnquadramento },
  { key: "regime_atual", label: "Regime Atual", tituloExcel: "Regime Atual", getValor: (c) => c.regime_atual, getRotulo: (v) => rotuloOpcao(REGIMES_MAO_DE_OBRA, v) },
  { key: "ocupante", label: "Ocupante", tituloExcel: "Ocupante", align: "left", sticky: true, width: 170, getValor: (c) => c.ocupante || "", getRotulo: (v) => v || "Vaga em aberto" },
  { key: "salario_bruto_atual", label: "Salário Bruto Atual", tituloExcel: "Salário Bruto Atual", align: "right", sticky: true, width: 160, getValor: (c) => c.salario_bruto_atual, getRotulo: rotuloMoeda, getExcel: (v) => (v ?? "") },
  { key: "sem_reajuste", label: "Sem Reajuste", tituloExcel: "Sem Reajuste no Ano", reajuste: true, getValor: (c) => !!c.sem_reajuste, getRotulo: rotuloBooleano },
  { key: "r1_ativo", label: "Reajuste 1", tituloExcel: "Reajuste 1 - Ativo", reajuste: true, getValor: (c) => !!reajusteAtual(c, 1).ativo, getRotulo: rotuloBooleano },
  { key: "r1_agrupamento", label: "Agrupamento", tituloExcel: "Reajuste 1 - Agrupamento", reajuste: true, getValor: (c) => reajusteAtual(c, 1).agrupamento, getRotulo: rotuloAgrupamento },
  { key: "r1_enquadramento", label: "Enquadramento", tituloExcel: "Reajuste 1 - Enquadramento", reajuste: true, getValor: (c) => reajusteAtual(c, 1).enquadramento, getRotulo: rotuloEnquadramento },
  { key: "r1_regime", label: "Regime", tituloExcel: "Reajuste 1 - Regime", reajuste: true, getValor: (c) => reajusteAtual(c, 1).regime, getRotulo: (v) => rotuloOpcao(REGIMES_MAO_DE_OBRA, v) },
  { key: "r1_salario", label: "Salário", tituloExcel: "Reajuste 1 - Salário", reajuste: true, getValor: (c) => reajusteAtual(c, 1).salario, getRotulo: rotuloMoeda, getExcel: (v) => (v ?? "") },
  { key: "r1_mes", label: "Mês Início", tituloExcel: "Reajuste 1 - Mês Início", reajuste: true, getValor: (c) => reajusteAtual(c, 1).mes, getRotulo: (v) => rotuloOpcao(MESES_MAO_DE_OBRA, v) },
  { key: "r2_ativo", label: "Reajuste 2", tituloExcel: "Reajuste 2 - Ativo", reajuste: true, getValor: (c) => !!reajusteAtual(c, 2).ativo, getRotulo: rotuloBooleano },
  { key: "r2_agrupamento", label: "Agrupamento", tituloExcel: "Reajuste 2 - Agrupamento", reajuste: true, getValor: (c) => reajusteAtual(c, 2).agrupamento, getRotulo: rotuloAgrupamento },
  { key: "r2_enquadramento", label: "Enquadramento", tituloExcel: "Reajuste 2 - Enquadramento", reajuste: true, getValor: (c) => reajusteAtual(c, 2).enquadramento, getRotulo: rotuloEnquadramento },
  { key: "r2_regime", label: "Regime", tituloExcel: "Reajuste 2 - Regime", reajuste: true, getValor: (c) => reajusteAtual(c, 2).regime, getRotulo: (v) => rotuloOpcao(REGIMES_MAO_DE_OBRA, v) },
  { key: "r2_salario", label: "Salário", tituloExcel: "Reajuste 2 - Salário", reajuste: true, getValor: (c) => reajusteAtual(c, 2).salario, getRotulo: rotuloMoeda, getExcel: (v) => (v ?? "") },
  { key: "r2_mes", label: "Mês Atualização", tituloExcel: "Reajuste 2 - Mês Atualização", reajuste: true, getValor: (c) => reajusteAtual(c, 2).mes, getRotulo: (v) => rotuloOpcao(MESES_MAO_DE_OBRA, v) },
  { key: "salario_bruto", label: "Salário Bruto", tituloExcel: "Salário Bruto (Dez)", getValor: (c) => c.salario_bruto, getRotulo: rotuloMoeda, getExcel: (v) => (v ?? "") },
  { key: "vigencia_inicio", label: "Vigência Início", tituloExcel: "Vigência Início", getValor: (c) => c.vigencia_inicio, getRotulo: (v) => rotuloOpcao(MESES_MAO_DE_OBRA, v) },
  { key: "vigencia_fim", label: "Vigência Fim", tituloExcel: "Vigência Fim", getValor: (c) => c.vigencia_fim, getRotulo: (v) => rotuloOpcao(MESES_MAO_DE_OBRA, v) },
];

// Visão executiva: a tabela principal mostra só o essencial (Cargo, Ocupante, Salário Bruto
// Atual) + Total calculado — o resto (Departamento, classificação, reajustes, vigência,
// composição de custo) fica no modal "Editar" (ver ModalEditarColaborador), para não poluir
// a lista com ~20 colunas de uso ocasional. Departamento já está fixado pelo seletor da
// sidebar (1 departamento por vez), então sai da tabela — editável só no modal. COLUNAS_TABELA
// continua completa para a Exportação Excel, que é um dump de todos os dados independente do
// que está visível na tela.
const CHAVES_VISAO_EXECUTIVA = ["cargo", "ocupante", "salario_bruto_atual"];
const COLUNAS_VISAO_EXECUTIVA = COLUNAS_TABELA.filter((col) => CHAVES_VISAO_EXECUTIVA.includes(col.key));

// Cargo/Função, Ocupante e Salário Bruto Atual ficam "travados" (sticky) à esquerda ao rolar a
// tabela horizontalmente até os meses — cada um precisa de um `left` = soma das larguras dos
// sticky anteriores, por isso a largura de cada coluna sticky é fixa (não é só um `minWidth`).
let _offsetStickyAcumulado = 0;
COLUNAS_VISAO_EXECUTIVA.forEach((col) => {
  if (!col.sticky) return;
  col.stickyLeft = _offsetStickyAcumulado;
  _offsetStickyAcumulado += col.width;
});
const STICKY_POR_CHAVE = Object.fromEntries(COLUNAS_VISAO_EXECUTIVA.filter((c) => c.sticky).map((c) => [c.key, c]));

// Estilo de uma célula do corpo (não cabeçalho) travada na coluna `chave` — usa o mesmo
// left/width calculados acima para o cabeçalho, para as duas ficarem sempre alinhadas.
function stickyTdStyle(chave, background) {
  const col = STICKY_POR_CHAVE[chave];
  return { position: "sticky", left: col.stickyLeft, width: col.width, minWidth: col.width, maxWidth: col.width, background, zIndex: 1 };
}

// Valores únicos de uma coluna (para o dropdown de filtro), com rótulo pronto para exibição.
function valoresUnicosColuna(lista, coluna) {
  const mapa = new Map();
  lista.forEach((c) => {
    const valor = coluna.getValor(c);
    const chave = valor === null || valor === undefined ? "__vazio__" : String(valor);
    if (!mapa.has(chave)) {
      mapa.set(chave, { valor, rotulo: coluna.getRotulo ? coluna.getRotulo(valor) : (valor === "" || valor === null || valor === undefined ? "—" : String(valor)) });
    }
  });
  return Array.from(mapa.values()).sort((a, b) => a.rotulo.localeCompare(b.rotulo, "pt-BR"));
}

// Visão genérica de mão de obra: recebe `departamentos` (lista de departamentos deste módulo —
// ver DEPARTAMENTOS_CUSTO_MAO_DE_OBRA / DEPARTAMENTOS_DESPESAS_MAO_DE_OBRA em
// constants/departamentosOrcamento.js) e exibe 1 departamento por vez, selecionado no dropdown
// da sidebar — igual a Custos Gerais × Despesas Gerais — para nunca misturar colaboradores de
// departamentos diferentes na mesma tabela.
// O backend não filtra por departamento; a filtragem e os KPIs agregados são calculados aqui.
export default function MaoDeObraView({ tituloModulo, departamentos }) {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [departamento, setDepartamento] = useState(departamentos?.[0]);
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [visao, setVisao] = useState("salario");
  const [colaboradorEditandoId, setColaboradorEditandoId] = useState(null);
  // Rascunho de contratação nova ainda não gravada — ver novoColaboradorRascunho/criarColaborador.
  const [criandoNovo, setCriandoNovo] = useState(false);

  // Filtros por coluna, estilo Excel: { [chaveColuna]: Set com os valores marcados }. Ausência
  // da chave (ou undefined) significa "sem filtro" — mostra todos os valores daquela coluna.
  const [filtros, setFiltros] = useState({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFiltros({}); }, [departamento, ano]);

  function atualizarFiltro(chave, novoConjunto) {
    setFiltros((f) => {
      const copia = { ...f };
      if (!novoConjunto) delete copia[chave]; else copia[chave] = novoConjunto;
      return copia;
    });
  }

  const colaboradoresFiltrados = useMemo(
    () => colaboradores.filter((c) => c.departamento === departamento),
    [colaboradores, departamento]
  );

  const valoresPorColuna = useMemo(
    () => Object.fromEntries(COLUNAS_VISAO_EXECUTIVA.map((col) => [col.key, valoresUnicosColuna(colaboradoresFiltrados, col)])),
    [colaboradoresFiltrados]
  );

  const filtrosAtivos = Object.keys(filtros).length;

  // Ordenação por coluna (Cargo, Ocupante, Salário): clique no título alterna crescente →
  // decrescente → sem ordenação (ordem original do backend). Vazios sempre ficam no fim.
  const [ordenacao, setOrdenacao] = useState(null); // { chave, direcao: "asc" | "desc" }

  function alternarOrdenacao(chave) {
    setOrdenacao((o) => {
      if (!o || o.chave !== chave) return { chave, direcao: "asc" };
      if (o.direcao === "asc") return { chave, direcao: "desc" };
      return null;
    });
  }

  const colaboradoresExibidos = useMemo(() => {
    const lista = colaboradoresFiltrados.filter((c) => COLUNAS_TABELA.every((col) => {
      const marcados = filtros[col.key];
      return !marcados || marcados.has(col.getValor(c));
    }));
    if (!ordenacao) return lista;
    const col = COLUNAS_TABELA.find((cl) => cl.key === ordenacao.chave);
    const sinal = ordenacao.direcao === "asc" ? 1 : -1;
    const vazio = (v) => v === null || v === undefined || v === "";
    return [...lista].sort((a, b) => {
      const va = col.getValor(a);
      const vb = col.getValor(b);
      if (vazio(va) || vazio(vb)) return vazio(va) - vazio(vb);
      const cmp = col.align === "right"
        ? Number(va) - Number(vb)
        : String(va).localeCompare(String(vb), "pt-BR", { sensitivity: "base", numeric: true });
      return cmp * sinal;
    });
  }, [colaboradoresFiltrados, filtros, ordenacao]);

  // Barra de rolagem horizontal duplicada acima da tabela, sincronizada com a rolagem real —
  // com muitas colunas, o usuário não precisa descer até o fim da tabela para rolar.
  const tabelaScrollRef = useRef(null);
  const topoScrollRef = useRef(null);
  const [larguraTabela, setLarguraTabela] = useState(0);

  useEffect(() => {
    const el = tabelaScrollRef.current;
    if (!el) return;
    const atualizarLargura = () => setLarguraTabela(el.scrollWidth);
    atualizarLargura();
    const obs = new ResizeObserver(atualizarLargura);
    obs.observe(el);
    return () => obs.disconnect();
  }, [colaboradoresExibidos, visao]);

  function sincronizarDoTopo() {
    if (tabelaScrollRef.current && topoScrollRef.current) tabelaScrollRef.current.scrollLeft = topoScrollRef.current.scrollLeft;
  }
  function sincronizarDaTabela() {
    if (tabelaScrollRef.current && topoScrollRef.current) topoScrollRef.current.scrollLeft = tabelaScrollRef.current.scrollLeft;
  }

  async function carregarTabela() {
    setLoading(true);
    setErro("");
    try {
      const lista = await getColaboradoresMaoDeObra(ano);
      setColaboradores(lista || []);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarTabela(); }, [ano]);

  // Dados do ano anterior — usados pelos KPIs de Custo Total ano anterior / Variação Anual.
  const [colaboradoresAnoAnterior, setColaboradoresAnoAnterior] = useState([]);
  useEffect(() => {
    let cancelado = false;
    getColaboradoresMaoDeObra(ano - 1)
      .then((lista) => { if (!cancelado) setColaboradoresAnoAnterior(lista || []); })
      .catch(() => { if (!cancelado) setColaboradoresAnoAnterior([]); });
    return () => { cancelado = true; };
  }, [ano]);

  function atualizarColaboradorLocal(atualizado) {
    setColaboradores((lista) => lista.map((c) => (c.id === atualizado.id ? atualizado : c)));
  }

  async function salvarCampo(colaborador, campo, valor) {
    try {
      const atualizado = await putColaboradorMaoDeObra(colaborador.id, { [campo]: valor }, ano);
      atualizarColaboradorLocal(atualizado);
    } catch (e) {
      setErro(e.message);
    }
  }

  // Campos "de classificação" editados no modal — Cargo/Ocupante/Salário Bruto Atual ficam na
  // linha da tabela (LinhaColaborador) e continuam salvando direto via salvarCampo, pois lá o
  // usuário já está preenchendo o dado real. O modal, por outro lado, muitas vezes é usado para
  // simular cenários (ex: "e se esse cargo virasse PJ?") — por isso ele NÃO salva a cada campo:
  // acumula tudo em um rascunho local e só grava (via salvarEdicaoColaborador) quando o usuário
  // clica em "Salvar alterações", evitando perder o dado real de uma simulação abandonada.
  // Cargo só é editável no modal em modo criação (Nova contratação); na edição não muda, então
  // nunca entra no diff.
  const CAMPOS_CLASSIFICACAO_MODAL = [
    "cargo", "departamento", "nivel_cargo", "modelo_trabalho", "agrupamento",
    "enquadramento", "regime_atual", "plano_previdencia", "vigencia_inicio", "vigencia_fim",
    "nova_contratacao",
  ];

  async function salvarEdicaoColaborador(original, draft) {
    const patch = {};
    CAMPOS_CLASSIFICACAO_MODAL.forEach((campo) => {
      if (JSON.stringify(draft[campo]) !== JSON.stringify(original[campo])) patch[campo] = draft[campo];
    });
    if (Object.keys(patch).length > 0) {
      const atualizado = await putColaboradorMaoDeObra(original.id, patch, ano);
      atualizarColaboradorLocal(atualizado);
    }
    for (const ordem of [1, 2]) {
      const antes = reajusteAtual(original, ordem);
      const depois = reajusteAtual(draft, ordem);
      if (JSON.stringify(antes) !== JSON.stringify(depois)) {
        const atualizado = await putReajusteMaoDeObra(original.id, ordem, depois, ano);
        atualizarColaboradorLocal(atualizado);
      }
    }
    if (!!draft.sem_reajuste !== !!original.sem_reajuste) {
      const atualizado = await putSemReajusteMaoDeObra(original.id, !!draft.sem_reajuste, ano);
      atualizarColaboradorLocal(atualizado);
    }
  }

  // Abre o modal com um rascunho local (novoColaboradorRascunho) — nada é gravado no backend
  // aqui. Antes o clique já disparava o POST e a linha entrava "solta" na tabela com os defaults
  // do backend (inclusive vigência início sempre Janeiro) mesmo se o usuário fechasse o modal sem
  // preencher nada. Agora o POST só acontece dentro de criarColaborador, quando o usuário altera
  // algo no modal e clica em "Salvar alterações" (mesmo botão que fica desabilitado até haver
  // alteração — ver `alterado` em ModalEditarColaborador).
  function novaLinha() {
    setColaboradorEditandoId(null);
    setCriandoNovo(true);
  }

  // Só chamada a partir do "Salvar alterações" do modal em modo criação: primeiro cria a linha
  // com os defaults do backend, depois aplica (via salvarEdicaoColaborador) só o que o usuário
  // de fato alterou em relação ao rascunho — reaproveitando o mesmo diff usado na edição normal.
  async function criarColaborador(draft) {
    const criado = await postColaboradorMaoDeObra({ ano_referencia: ano, departamento: draft.departamento || departamento });
    setColaboradores((lista) => [...lista, criado]);
    await salvarEdicaoColaborador(criado, draft);
  }

  // Colaborador que veio de ano anterior é desligado a partir do ano da tela (o histórico fica);
  // só no ano de entrada a linha é excluída de vez — e aí some de todos os anos.
  async function excluirLinha(colaborador) {
    const nome = `"${colaborador.cargo || "colaborador"}" (${colaborador.ocupante || "vaga em aberto"})`;
    const mensagem = colaborador.ano_entrada < ano
      ? `Desligar ${nome} a partir de ${ano}? Ele deixa de aparecer em ${ano} e nos anos seguintes; os dados de ${colaborador.ano_entrada} a ${ano - 1} são mantidos.`
      : `Excluir a linha de ${nome}? Ela será removida de ${ano} e de todos os anos seguintes. Essa ação não pode ser desfeita.`;
    if (!window.confirm(mensagem)) return;
    try {
      await deleteColaboradorMaoDeObra(colaborador.id, ano);
      setColaboradores((lista) => lista.filter((c) => c.id !== colaborador.id));
    } catch (e) {
      setErro(e.message);
    }
  }

  // Sempre relido da lista (não guardado por referência) para refletir a versão mais recente
  // (ex: campos calculados) assim que o modal salvar e a tabela recarregar.
  const colaboradorEmEdicao = colaboradorEditandoId ? colaboradores.find((c) => c.id === colaboradorEditandoId) || null : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rascunhoNovo = useMemo(() => (criandoNovo ? novoColaboradorRascunho(departamento, ano) : null), [criandoNovo]);

  const visaoAtual = VISOES_MAO_DE_OBRA.find((v) => v.id === visao) || VISOES_MAO_DE_OBRA[3];

  const totaisPorMes = useMemo(() => {
    const totais = Array(12).fill(0);
    colaboradoresExibidos.forEach((c) => {
      const meses = c[visaoAtual.campoMensal] || {};
      for (let mes = 1; mes <= 12; mes++) totais[mes - 1] += Number(meses[mes] || 0);
    });
    return totais;
  }, [colaboradoresExibidos, visaoAtual]);
  const totalGeralVisao = totaisPorMes.reduce((acc, v) => acc + v, 0);

  function exportarParaExcel() {
    const linhas = colaboradoresExibidos.map((c) => {
      const linha = {};
      COLUNAS_TABELA.forEach((col) => {
        const valor = col.getValor(c);
        linha[col.tituloExcel] = col.getExcel ? col.getExcel(valor) : (col.getRotulo ? col.getRotulo(valor) : (valor ?? ""));
      });
      const meses = c[visaoAtual.campoMensal] || {};
      MESES_ABREV.forEach((m, i) => { linha[m] = Number(meses[i + 1] || 0); });
      linha["Total"] = c[visaoAtual.campoTotal];
      return linha;
    });
    linhas.push({
      ...Object.fromEntries(COLUNAS_TABELA.map((col) => [col.tituloExcel, ""])),
      "Cargo/Função": "Total Geral",
      ...Object.fromEntries(MESES_ABREV.map((m, i) => [m, totaisPorMes[i]])),
      Total: totalGeralVisao,
    });
    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, tituloModulo.slice(0, 31));
    XLSX.writeFile(workbook, `${tituloModulo}_${departamento}_${visaoAtual.label}_${ano}`.replace(/[^\w]+/g, "_") + ".xlsx");
  }

  // KPIs comparam sempre o departamento inteiro do ano (colaboradoresFiltrados), sem sofrer
  // efeito dos filtros de coluna da tabela.
  const colaboradoresAnoAnteriorFiltrados = useMemo(
    () => colaboradoresAnoAnterior.filter((c) => c.departamento === departamento),
    [colaboradoresAnoAnterior, departamento]
  );
  const custoAnoAtual = useMemo(() => custoTotalFolha(colaboradoresFiltrados), [colaboradoresFiltrados]);
  const custoAnoAnterior = useMemo(() => custoTotalFolha(colaboradoresAnoAnteriorFiltrados), [colaboradoresAnoAnteriorFiltrados]);
  const totalColaboradoresDespesas = colaboradoresFiltrados.length;
  const colaboradoresComReajuste = useMemo(() => colaboradoresFiltrados.filter(reajustePreenchido).length, [colaboradoresFiltrados]);
  const progressoSubmissao = totalColaboradoresDespesas > 0 ? (colaboradoresComReajuste / totalColaboradoresDespesas) * 100 : 0;
  const variacaoAnual = custoAnoAtual - custoAnoAnterior;
  const direcaoVariacaoAnual = variacaoAnual > 0 ? "up" : variacaoAnual < 0 ? "down" : "flat";
  const percentVariacaoAnual = custoAnoAnterior > 0 ? (variacaoAnual / custoAnoAnterior) * 100 : 0;

  const kpiItems = [
    { label: `Custo Total Orçado ${ano}`, value: loading ? "—" : formatCurrency(custoAnoAtual), background: C.brand },
    { label: `Custo Total ${ano - 1}`, value: loading ? "—" : formatCurrency(custoAnoAnterior), color: C.muted },
    {
      label: "Progresso Orçamentário", value: loading ? "—" : `${colaboradoresComReajuste}/${totalColaboradoresDespesas}`,
      sub: "colaboradores com reajuste informado",
      accessory: !loading && <GaugeProgresso percent={progressoSubmissao} color={C.brand} trackColor={C.border} />,
    },
    {
      label: "Variação Anual",
      value: loading ? "—" : `${variacaoAnual > 0 ? "+" : ""}${formatCurrency(variacaoAnual)}`,
      color: direcaoVariacaoAnual === "up" ? C.success : direcaoVariacaoAnual === "down" ? C.danger : C.text,
      sub: loading ? undefined : `vs. ${ano - 1}: ${variacaoAnual === 0 ? "Sem alteração" : `${percentVariacaoAnual > 0 ? "+" : ""}${percentVariacaoAnual.toFixed(1)}%`}`,
      accessory: !loading && <TrendAccessory direcao={direcaoVariacaoAnual} C={C} />,
    },
  ];

  if (!departamentos || departamentos.length === 0) {
    return (
      <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
        <SideBarNavGestaoOrcamentaria isLight={isLight} />
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 14 }}>
          Nenhum departamento disponível para o seu perfil em {tituloModulo}.
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria
        isLight={isLight}
        departamentos={departamentos}
        departamentoAtual={departamento}
        onSelecionarDepartamento={setDepartamento}
      />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· {tituloModulo}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", fontSize: 13, fontWeight: 600, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={departamento}>
              {departamento}
            </span>
            <select value={ano} onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}>
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          <KpiRowOrcamento items={kpiItems} C={C} />

          <section>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>{tituloModulo} · {departamento} · {ano}</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <button onClick={exportarParaExcel} disabled={loading || colaboradoresExibidos.length === 0} title="Exportar dados para Excel (todas as colunas)"
                  style={{ ...btnStyle(C.success, true), opacity: loading || colaboradoresExibidos.length === 0 ? 0.45 : 1, cursor: loading || colaboradoresExibidos.length === 0 ? "not-allowed" : "pointer" }}>
                  <FileSpreadsheet size={15} /> Exportar Excel
                </button>
                {filtrosAtivos > 0 && (
                  <button onClick={() => setFiltros({})} title="Remover todos os filtros de coluna"
                    style={{ ...btnStyle(C.danger, true), padding: "9px 12px" }}>
                    <X size={14} /> Limpar filtros ({filtrosAtivos})
                  </button>
                )}
                <div style={{ display: "flex", gap: 6, background: C.bg, padding: 4, borderRadius: 10, border: `1px solid ${C.border}` }}>
                  {VISOES_MAO_DE_OBRA.map((v) => (
                    <button key={v.id} onClick={() => setVisao(v.id)} style={{
                      padding: "7px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                      background: visao === v.id ? C.brand : "transparent", color: visao === v.id ? "#fff" : C.text,
                      fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                    }}>
                      {v.label}
                    </button>
                  ))}
                </div>
                <button onClick={novaLinha} style={btnStyle(C.brand)}><Plus size={15} /> Nova contratação</button>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
            ) : colaboradoresFiltrados.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                Nenhum colaborador cadastrado para {departamento} em {ano}. Clique em "Nova linha" para começar.
              </div>
            ) : colaboradoresExibidos.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                Nenhum colaborador corresponde aos filtros aplicados.{" "}
                <button onClick={() => setFiltros({})} style={{ color: C.brand, background: "none", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: "inherit" }}>Limpar filtros</button>
              </div>
            ) : (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                <div ref={topoScrollRef} onScroll={sincronizarDoTopo} style={{ overflowX: "auto", overflowY: "hidden", height: 14 }}>
                  <div style={{ width: larguraTabela, height: 1 }} />
                </div>
                <div ref={tabelaScrollRef} onScroll={sincronizarDaTabela} style={{ overflow: "auto", maxHeight: "65vh" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1700 }}>
                    <thead>
                      <tr>
                        <th style={thStyle(C)}>Ações</th>
                        {COLUNAS_VISAO_EXECUTIVA.map((col) => (
                          <Fragment key={col.key}>
                            <th style={col.sticky
                              ? { ...thStyle(C, col.reajuste, col.align), position: "sticky", left: col.stickyLeft, top: 0, zIndex: 4, width: col.width, minWidth: col.width, maxWidth: col.width }
                              : thStyle(C, col.reajuste, col.align)}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                                <button onClick={() => alternarOrdenacao(col.key)} title={`Ordenar por ${col.label}`}
                                  style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, cursor: "pointer", color: "inherit", font: "inherit" }}>
                                  {col.label}
                                  {ordenacao?.chave === col.key
                                    ? (ordenacao.direcao === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
                                    : <ArrowUpDown size={12} style={{ opacity: 0.5 }} />}
                                </button>
                                <FiltroColuna C={C} valores={valoresPorColuna[col.key] || []} selecionados={filtros[col.key]}
                                  onChange={(novo) => atualizarFiltro(col.key, novo)} />
                              </span>
                            </th>
                            {col.key === "cargo" && (
                              <th style={thStyle(C)}>Status Reajuste</th>
                            )}
                          </Fragment>
                        ))}
                        {MESES_ABREV.map((m) => <th key={m} style={thStyle(C, false, "right")}>{m}</th>)}
                        <th style={thStyle(C, false, "right")}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {colaboradoresExibidos.map((c, idx) => (
                        <LinhaColaborador
                          key={c.id} C={C} colaborador={c} idx={idx} visaoAtual={visaoAtual}
                          onSalvarCampo={(campo, valor) => salvarCampo(c, campo, valor)}
                          onEditar={() => setColaboradorEditandoId(c.id)}
                          onExcluir={() => excluirLinha(c)}
                        />
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 800, borderTop: `2px solid ${C.border}` }}>
                        <td style={tdStyle(C)} />
                        <td style={{ ...tdStyle(C, true), position: "sticky", left: 0, textAlign: "left" }} colSpan={COLUNAS_VISAO_EXECUTIVA.length + 1}>Total Geral · {visaoAtual.label}</td>
                        {totaisPorMes.map((v, i) => <td key={i} style={tdStyle(C, true)}>{formatCurrency(v)}</td>)}
                        <td style={tdStyle(C, true)}>{formatCurrency(totalGeralVisao)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>

      {(colaboradorEmEdicao || rascunhoNovo) && (
        <ModalEditarColaborador
          key={colaboradorEmEdicao ? colaboradorEmEdicao.id : "novo"}
          C={C} colaborador={colaboradorEmEdicao || rascunhoNovo} novo={!colaboradorEmEdicao}
          departamentos={[departamento]} visaoAtual={visaoAtual} ano={ano}
          onClose={() => { setColaboradorEditandoId(null); setCriandoNovo(false); }}
          onSalvar={(draft) => (colaboradorEmEdicao ? salvarEdicaoColaborador(colaboradorEmEdicao, draft) : criarColaborador(draft))}
        />
      )}
    </div>
  );
}

// ── Linha da tabela (visão executiva) ────────────────────────────────────────
// Só o essencial fica na linha: Cargo, Ocupante e Salário Bruto Atual (editáveis, travados à
// esquerda ao rolar) + os meses e o Total calculado. Departamento, classificação, reajustes,
// vigência e composição de custo ficam no modal "Editar" (ver ModalEditarColaborador), aberto
// pelo ícone de lápis.

function LinhaColaborador({ C, colaborador: c, idx, visaoAtual, onSalvarCampo, onEditar, onExcluir }) {
  const bgLinha = idx % 2 === 0 ? C.card : C.rowAlt;
  const meses = c[visaoAtual.campoMensal] || {};

  return (
    <tr>
      <td style={{ ...tdStyle(C), background: bgLinha }}>
        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
          <button onClick={onEditar} title="Editar detalhes" style={iconBtnStyle(C)}><Pencil size={13} /></button>
          <button onClick={onExcluir} title="Excluir linha" style={iconBtnStyle(C, true)}><Trash2 size={13} /></button>
        </div>
      </td>
      <td style={{ ...tdStyle(C), ...stickyTdStyle("cargo", bgLinha), textAlign: "left" }}>
        <CampoTexto C={C} valor={c.cargo} onSalvar={(v) => onSalvarCampo("cargo", v)} larguraMin={170} />
      </td>
      <td style={{ ...tdStyle(C), background: bgLinha, textAlign: "center" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
          <BadgeStatusReajuste C={C} preenchido={reajustePreenchido(c)} semReajuste={!!c.sem_reajuste} />
          {!reajustePreenchido(c) && (
            <span style={{ fontSize: 9.5, color: C.muted }}>Clique em editar para inserir ou marcar sem reajuste</span>
          )}
          {c.projetado && (
            <span style={{ fontSize: 9.5, color: C.muted, fontStyle: "italic" }}
              title="Nada cadastrado neste ano — valores carregados do último ano conhecido (corrigidos pelo índice de reajuste, se houver).">
              Projetado · desde {c.ano_entrada}
            </span>
          )}
        </div>
      </td>
      <td style={{ ...tdStyle(C), ...stickyTdStyle("ocupante", bgLinha), textAlign: "left" }}>
        <CampoTexto C={C} valor={c.ocupante || ""} onSalvar={(v) => onSalvarCampo("ocupante", v || null)} placeholder="Vaga em aberto" larguraMin={150} />
      </td>
      <td style={{ ...tdStyle(C), ...stickyTdStyle("salario_bruto_atual", bgLinha), textAlign: "right" }}>
        <CampoSalarioTabela C={C} valor={c.salario_bruto_atual} excecao={c.salario_excecao}
          onSalvarValor={(v) => onSalvarCampo("salario_bruto_atual", v)}
          onToggleExcecao={(v) => onSalvarCampo("salario_excecao", v)} />
      </td>
      {Array.from({ length: 12 }, (_, i) => i + 1).map((mes) => (
        <td key={mes} style={{ ...tdStyle(C), background: bgLinha, color: C.muted, cursor: "not-allowed" }} title="Calculado automaticamente">
          {formatCurrency(meses[mes] || 0)}
        </td>
      ))}
      <td style={{ ...tdStyle(C), background: bgLinha, fontWeight: 700 }}>{formatCurrency(c[visaoAtual.campoTotal] || 0)}</td>
    </tr>
  );
}

// Badge "Status Reajuste" — verde "Inserido" quando o colaborador já tem Reajuste 1 e/ou 2
// preenchido (ou "Sem reajuste", quando marcado que não terá reajuste no ano), vermelho
// "Não inserido" caso contrário.
function BadgeStatusReajuste({ C, preenchido, semReajuste }) {
  const cor = preenchido ? C.success : C.danger;
  return (
    <span style={{
      display: "inline-block", padding: "3px 9px", borderRadius: 999, fontSize: 10.5, fontWeight: 700,
      background: `${cor}22`, color: cor, whiteSpace: "nowrap",
    }}>
      {semReajuste ? "Sem reajuste" : preenchido ? "Inserido" : "Não inserido"}
    </span>
  );
}

function ComposicaoCusto({ C, colaborador: c }) {
  return (
    <div style={{ display: "flex", gap: 32, flexWrap: "wrap", fontSize: 12 }}>
      <div>
        <p style={{ margin: "0 0 6px", fontWeight: 700, color: C.muted, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.06em" }}>Salário (dez/{c.ano_referencia})</p>
        <p style={{ margin: 0, fontWeight: 700, color: C.text }}>{formatCurrency(c.salario_bruto)}</p>
      </div>
      <div>
        <p style={{ margin: "0 0 6px", fontWeight: 700, color: C.muted, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.06em" }}>Encargos</p>
        {c.composicao_encargos.length === 0 ? (
          <p style={{ margin: 0, color: C.muted }}>Regime PJ — sem encargos</p>
        ) : c.composicao_encargos.map((item) => (
          <p key={item.codigo} style={{ margin: "0 0 2px", color: C.text }}>
            {item.label} ({formatPercent(item.percentual * 100)}): <strong>{formatCurrency(item.valor)}</strong>
            {item.incluir_no_total === false && <span style={{ color: C.muted, fontStyle: "italic" }}> · fora do total</span>}
          </p>
        ))}
      </div>
      <div>
        <p style={{ margin: "0 0 6px", fontWeight: 700, color: C.muted, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.06em" }}>Benefícios</p>
        {c.composicao_beneficios.length === 0 ? (
          <p style={{ margin: 0, color: C.muted }}>Nenhum benefício aplicável</p>
        ) : c.composicao_beneficios.map((item) => (
          <p key={item.codigo} style={{ margin: "0 0 2px", color: C.text }}>{item.label}: <strong>{formatCurrency(item.valor)}</strong></p>
        ))}
      </div>
    </div>
  );
}

// ── Modal: Editar detalhes do colaborador ────────────────────────────────────
// Reúne tudo que saiu da linha da tabela: classificação, os 2 reajustes, vigência,
// distribuição mensal calculada e composição de custo. Ao contrário das células da tabela
// (que salvam sozinhas no onBlur), os campos aqui só ficam em um rascunho local — o modal
// costuma ser usado para simular cenários, e salvar a cada tecla apagaria o dado real de uma
// simulação que o usuário decidiu não aplicar. Só grava no backend ao clicar "Salvar alterações".

function ModalEditarColaborador({ C, colaborador: c, novo, departamentos, visaoAtual, ano, onSalvar, onClose }) {
  const [draft, setDraft] = useState(() => clonarColaborador(c));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  // Tabela salarial do ano (já corrigida pelo índice quando herdada) — só para mostrar, a título
  // de referência, o salário que corresponde ao Agrupamento × Enquadramento escolhidos no modal.
  // O salário real continua sendo calculado/gravado pelo backend.
  const [tabelaSalarial, setTabelaSalarial] = useState(null);
  useEffect(() => {
    let ativo = true;
    getTabelaSalarialMaoDeObra(ano)
      .then((t) => { if (ativo) setTabelaSalarial(t); })
      .catch(() => { if (ativo) setTabelaSalarial(null); });
    return () => { ativo = false; };
  }, [ano]);
  function salarioDaTabela(agrupamento, enquadramento) {
    if (!tabelaSalarial || agrupamento == null || enquadramento == null) return null;
    const item = tabelaSalarial.itens.find((i) => Number(i.agrupamento) === Number(agrupamento)
      && Math.abs(Number(i.enquadramento) - Number(enquadramento)) < 0.001);
    return item ? Number(item.valor_referencia) : null;
  }
  const salarioTabela = salarioDaTabela(draft.agrupamento, draft.enquadramento);

  const r1 = reajusteAtual(draft, 1);
  const r2 = reajusteAtual(draft, 2);
  const meses = c[visaoAtual.campoMensal] || {};
  const alterado = JSON.stringify(draft) !== JSON.stringify(clonarColaborador(c));
  // Veio de um ano anterior: a edição vale a partir de janeiro deste ano, e início de vigência /
  // contratação nova pertencem ao ano de entrada (o backend recusa alterá-los aqui).
  const herdado = !novo && c.ano_entrada < c.ano_referencia;

  function atualizarCampo(campo, valor) {
    setDraft((d) => ({ ...d, [campo]: valor }));
  }
  // Reajuste ativo e "sem reajuste no ano" são excludentes — marcar um desmarca o outro.
  function atualizarReajuste(ordem, patch) {
    setDraft((d) => ({
      ...d,
      [`reajuste_${ordem}`]: { ...reajusteAtual(d, ordem), ...patch },
      ...(patch.ativo ? { sem_reajuste: false } : {}),
    }));
  }
  function marcarSemReajuste(valor) {
    setDraft((d) => (valor
      ? { ...d, sem_reajuste: true, reajuste_1: { ...reajusteAtual(d, 1), ativo: false }, reajuste_2: { ...reajusteAtual(d, 2), ativo: false } }
      : { ...d, sem_reajuste: false }));
  }

  function tentarFechar() {
    if (alterado && !window.confirm("Existem alterações não salvas neste colaborador. Deseja descartá-las?")) return;
    onClose();
  }

  async function salvar() {
    setSalvando(true);
    setErro("");
    try {
      await onSalvar(draft);
      onClose();
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth: 820, boxShadow: "0 24px 64px rgba(0,0,0,0.4)", maxHeight: "90vh", overflowY: "auto", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
          <div>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{(novo ? draft.cargo : c.cargo) || "Cargo não informado"}</p>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>{c.ocupante || "Vaga em aberto"} · {c.departamento}</p>
          </div>
          <button onClick={tentarFechar} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <p style={{ margin: 0, fontSize: 11, color: C.muted, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px" }}>
            {novo
              ? "Nada é gravado enquanto esta janela estiver aberta — a contratação só é criada ao clicar em \"Criar contratação\" abaixo."
              : "As alterações abaixo só são gravadas ao clicar em \"Salvar alterações\" — use à vontade para simular cenários sem risco de sobrescrever os dados reais."}
            {herdado && ` Colaborador ativo desde ${c.ano_entrada}: o que for alterado aqui vale a partir de janeiro de ${c.ano_referencia}, sem mudar os anos anteriores.`}
          </p>

          <ModalSecao C={C} titulo="Classificação">
            {novo && (
              <div style={{ marginBottom: 12 }}>
                <CampoModal C={C} label="Cargo/Função">
                  <input type="text" autoFocus placeholder="Ex: Analista Financeiro" value={draft.cargo || ""}
                    onChange={(e) => atualizarCampo("cargo", e.target.value)} style={celulaInputStyle(C)} />
                </CampoModal>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
              <CampoModal C={C} label="Departamento">
                <CampoSelect C={C} valor={draft.departamento} onChange={(v) => atualizarCampo("departamento", v)} opcoes={departamentos.map((d) => ({ value: d, label: d }))} />
              </CampoModal>
              <CampoModal C={C} label="Nível Cargo">
                <CampoSelect C={C} valor={draft.nivel_cargo} onChange={(v) => atualizarCampo("nivel_cargo", v)} opcoes={NIVEIS_CARGO} />
              </CampoModal>
              <CampoModal C={C} label="Modelo de Trabalho">
                <CampoSelect C={C} valor={draft.modelo_trabalho} onChange={(v) => atualizarCampo("modelo_trabalho", v)} opcoes={MODELOS_TRABALHO} />
              </CampoModal>
              <CampoModal C={C} label="Agrupamento">
                <CampoSelectAgrupamento C={C} valor={draft.agrupamento} onChange={(v) => atualizarCampo("agrupamento", v)} />
              </CampoModal>
              <CampoModal C={C} label="Enquadramento">
                <CampoSelectEnquadramento C={C} valor={draft.enquadramento} onChange={(v) => atualizarCampo("enquadramento", v)} />
              </CampoModal>
              <CampoModal C={C} label="Regime Atual">
                <CampoSelect C={C} valor={draft.regime_atual} onChange={(v) => atualizarCampo("regime_atual", v)} opcoes={REGIMES_MAO_DE_OBRA} />
              </CampoModal>
              <CampoModal C={C} label={`Salário (tabela ${ano})`}>
                <span title="Valor de referência da tabela salarial para o Agrupamento × Enquadramento escolhidos"
                  style={{ ...celulaInputStyle(C), display: "block", textAlign: "right", fontWeight: 600, color: salarioTabela != null ? C.text : C.muted }}>
                  {salarioTabela != null ? formatCurrency(salarioTabela) : "—"}
                </span>
              </CampoModal>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer", marginTop: 10 }}>
              <input type="checkbox" checked={!!draft.plano_previdencia} onChange={(e) => atualizarCampo("plano_previdencia", e.target.checked)} style={{ cursor: "pointer" }} />
              Possui plano de previdência
            </label>
          </ModalSecao>

          <ModalSecao C={C} titulo="Vigência">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <CampoModal C={C} label={herdado ? `Vigência Início (ativo desde ${c.ano_entrada})` : "Vigência Início"}>
                <CampoSelect C={C} valor={draft.vigencia_inicio} onChange={(v) => atualizarCampo("vigencia_inicio", Number(v))} opcoes={MESES_MAO_DE_OBRA} disabled={herdado} />
              </CampoModal>
              <CampoModal C={C} label="Vigência Fim">
                <CampoSelect C={C} valor={draft.vigencia_fim} onChange={(v) => atualizarCampo("vigencia_fim", Number(v))} opcoes={MESES_MAO_DE_OBRA} />
              </CampoModal>
            </div>
            {!herdado && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer", marginTop: 10 }}>
                <input type="checkbox" checked={!!draft.nova_contratacao} onChange={(e) => atualizarCampo("nova_contratacao", e.target.checked)} style={{ cursor: "pointer" }} />
                É uma contratação nova (conta em Vigência Início na Auditoria de Kits de Admissão)
              </label>
            )}
            {draft.nova_contratacao && (
              <p style={{ margin: "6px 0 0", fontSize: 11, color: C.muted }}>
                Confirme que "Vigência Início" acima é o mês real da contratação — não deixe no padrão (Janeiro) se a pessoa começou em outro mês.
              </p>
            )}
          </ModalSecao>

          <ModalSecao C={C} titulo="Status Reajuste">
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer" }}>
              <input type="checkbox" checked={!!draft.sem_reajuste} onChange={(e) => marcarSemReajuste(e.target.checked)} style={{ cursor: "pointer" }} />
              Este colaborador não terá reajuste em {c.ano_referencia}
            </label>
            <p style={{ margin: "6px 0 0", fontSize: 11, color: C.muted }}>
              Conta como reajuste inserido no Progresso Orçamentário. Marcar desativa os Reajustes 1 e 2 abaixo; ativar um reajuste desmarca esta opção.
            </p>
          </ModalSecao>

          <ModalSecaoReajuste C={C} titulo="Reajuste 1" reajuste={r1} rotuloMes="Mês Início"
            onAtivo={(v) => atualizarReajuste(1, { ativo: v })}
            onAgrupamento={(v) => atualizarReajuste(1, { agrupamento: v })}
            onEnquadramento={(v) => atualizarReajuste(1, { enquadramento: v })}
            onRegime={(v) => atualizarReajuste(1, { regime: v || null })}
            onSalarioValor={(v) => atualizarReajuste(1, { salario: v })}
            onSalarioExcecao={(v) => atualizarReajuste(1, { salario_excecao: v })}
            salarioTabela={salarioDaTabela(r1.agrupamento, r1.enquadramento)}
            onMes={(v) => atualizarReajuste(1, { mes: v ? Number(v) : null })}
          />

          <ModalSecaoReajuste C={C} titulo="Reajuste 2" reajuste={r2} rotuloMes="Mês Atualização"
            onAtivo={(v) => atualizarReajuste(2, { ativo: v })}
            onAgrupamento={(v) => atualizarReajuste(2, { agrupamento: v })}
            onEnquadramento={(v) => atualizarReajuste(2, { enquadramento: v })}
            onRegime={(v) => atualizarReajuste(2, { regime: v || null })}
            onSalarioValor={(v) => atualizarReajuste(2, { salario: v })}
            onSalarioExcecao={(v) => atualizarReajuste(2, { salario_excecao: v })}
            salarioTabela={salarioDaTabela(r2.agrupamento, r2.enquadramento)}
            onMes={(v) => atualizarReajuste(2, { mes: v ? Number(v) : null })}
          />

          {novo ? (
            <ModalSecao C={C} titulo="Distribuição Mensal e Composição de Custo">
              <p style={{ margin: 0, fontSize: 11, color: C.muted }}>
                Calculadas pelo backend a partir da tabela salarial — disponíveis para consulta aqui assim que a contratação for criada.
              </p>
            </ModalSecao>
          ) : (
            <>
              <ModalSecao C={C} titulo={`Distribuição Mensal — ${visaoAtual.label} (calculado)`}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(64px, 1fr))", gap: 6, overflowX: "auto" }}>
                  {MESES_ABREV.map((m, i) => (
                    <div key={m} style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center", padding: "6px 4px", background: C.bg, borderRadius: 6, border: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 9, color: C.muted, fontWeight: 600 }}>{m}</span>
                      <span style={{ fontSize: 11, color: C.text, fontWeight: 600 }}>{formatCurrency(meses[i + 1] || 0)}</span>
                    </div>
                  ))}
                </div>
                <p style={{ margin: "8px 0 0", fontSize: 11, color: C.muted }}>
                  Salário Bruto (Dez): <strong style={{ color: C.text }}>{formatCurrency(c.salario_bruto)}</strong> · Total {visaoAtual.label}: <strong style={{ color: C.text }}>{formatCurrency(c[visaoAtual.campoTotal] || 0)}</strong>
                  {alterado && " · recalculado após salvar"}
                </p>
              </ModalSecao>

              <ModalSecao C={C} titulo="Composição de Custo">
                <ComposicaoCusto C={C} colaborador={c} />
              </ModalSecao>
            </>
          )}

          {erro && <p style={{ margin: 0, fontSize: 12, color: C.danger }}>{erro}</p>}
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 20, position: "sticky", bottom: 0, background: C.card, paddingTop: 10 }}>
          <button onClick={tentarFechar} disabled={salvando}
            style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: salvando ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
            Cancelar
          </button>
          <button onClick={salvar} disabled={salvando || !alterado}
            style={{ flex: 1, background: C.brand, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: salvando || !alterado ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: salvando || !alterado ? 0.6 : 1 }}>
            {salvando ? "Salvando..." : (novo ? "Criar contratação" : "Salvar alterações")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalSecao({ C, titulo, children }) {
  return (
    <div>
      <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>{titulo}</p>
      {children}
    </div>
  );
}

function CampoModal({ C, label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, color: C.muted }}>{label}</label>
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, background: C.bg }}>{children}</div>
    </div>
  );
}

function ModalSecaoReajuste({ C, titulo, reajuste, rotuloMes, salarioTabela, onAtivo, onAgrupamento, onEnquadramento, onRegime, onSalarioValor, onSalarioExcecao, onMes }) {
  return (
    <ModalSecao C={C} titulo={titulo}>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: C.text, cursor: "pointer", marginBottom: 10 }}>
        <input type="checkbox" checked={!!reajuste.ativo} onChange={(e) => onAtivo(e.target.checked)} style={{ cursor: "pointer" }} />
        Reajuste ativo
      </label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <CampoModal C={C} label="Agrupamento">
          <CampoSelectAgrupamento C={C} valor={reajuste.agrupamento} onChange={onAgrupamento} permiteVazio />
        </CampoModal>
        <CampoModal C={C} label="Enquadramento">
          <CampoSelectEnquadramento C={C} valor={reajuste.enquadramento} onChange={onEnquadramento} permiteVazio />
        </CampoModal>
        <CampoModal C={C} label="Regime">
          <CampoSelect C={C} valor={reajuste.regime || ""} onChange={onRegime} opcoes={REGIMES_MAO_DE_OBRA} permiteVazio />
        </CampoModal>
        <CampoModal C={C} label="Salário">
          {/* Sem exceção, mostra o valor da tabela para o Agrupamento × Enquadramento do rascunho
              (o gravado só é recalculado pelo backend ao salvar) — só referência. */}
          <CampoSalarioTabela C={C} valor={reajuste.salario_excecao ? reajuste.salario : (salarioTabela ?? reajuste.salario)}
            excecao={reajuste.salario_excecao} onSalvarValor={onSalarioValor} onToggleExcecao={onSalarioExcecao} />
        </CampoModal>

        <CampoModal C={C} label={rotuloMes}>
          <CampoSelect C={C} valor={reajuste.mes || ""} onChange={onMes} opcoes={MESES_MAO_DE_OBRA} permiteVazio />
        </CampoModal>
      </div>
    </ModalSecao>
  );
}

function miniBtnStyle(C, disabled) {
  return {
    background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6,
    padding: "4px 8px", fontSize: 10, fontWeight: 600, color: disabled ? C.muted : C.brand,
    cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: disabled ? 0.5 : 1,
  };
}

// ── Células editáveis genéricas ──────────────────────────────────────────────

function CampoTexto({ C, valor, onSalvar, placeholder, larguraMin }) {
  const [texto, setTexto] = useState(valor);
  const [editando, setEditando] = useState(false);
  useEffect(() => { if (!editando) setTexto(valor); }, [valor, editando]);
  return (
    <input type="text" placeholder={placeholder} value={editando ? texto : (valor || "")}
      onFocus={() => setEditando(true)}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => { setEditando(false); if (texto !== valor) onSalvar(texto); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      style={larguraMin ? { ...celulaInputStyle(C), minWidth: larguraMin } : celulaInputStyle(C)} />
  );
}

function CampoNumero({ C, valor, onSalvar }) {
  const [texto, setTexto] = useState(String(valor ?? 0));
  const [editando, setEditando] = useState(false);
  useEffect(() => { if (!editando) setTexto(String(valor ?? 0)); }, [valor, editando]);
  function confirmar() {
    setEditando(false);
    const novo = parseFloat(String(texto).replace(",", ".")) || 0;
    if (novo !== Number(valor || 0)) onSalvar(novo);
  }
  return (
    <input type="text" inputMode="decimal" value={editando ? texto : formatCurrency(valor || 0)}
      onFocus={() => { setEditando(true); setTexto(String(valor ?? 0)); }}
      onChange={(e) => setTexto(e.target.value)} onBlur={confirmar}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      style={{ ...celulaInputStyle(C), textAlign: "right" }} />
  );
}

function CampoSelect({ C, valor, onChange, opcoes, permiteVazio, disabled }) {
  return (
    <select value={valor ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled}
      style={{ ...celulaInputStyle(C), ...(disabled ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}>
      {permiteVazio && <option value="">—</option>}
      {opcoes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function CampoSelectAgrupamento({ C, valor, onChange, permiteVazio }) {
  return (
    <select value={valor ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} style={celulaInputStyle(C)}>
      {permiteVazio && <option value="">—</option>}
      {AGRUPAMENTOS.map((a) => <option key={a} value={a}>{a}</option>)}
    </select>
  );
}

function CampoSelectEnquadramento({ C, valor, onChange, permiteVazio }) {
  return (
    <select value={valor ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} style={celulaInputStyle(C)}>
      {permiteVazio && <option value="">—</option>}
      {ENQUADRAMENTOS.map((e) => <option key={e} value={e}>{formatPercent(e * 100)}</option>)}
    </select>
  );
}

function CampoCheckbox({ C, valor, onChange }) {
  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <input type="checkbox" checked={!!valor} onChange={(e) => onChange(e.target.checked)} style={{ cursor: "pointer" }} />
    </div>
  );
}

// Salário travado em tabela_salarial[agrupamento][enquadramento] — só libera edição
// manual quando a exceção está marcada (ver Ajuste 1 do documento de ajuste).
function CampoSalarioTabela({ C, valor, excecao, onSalvarValor, onToggleExcecao }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 84 }}>
      {excecao ? (
        <CampoNumero C={C} valor={valor} onSalvar={onSalvarValor} />
      ) : (
        <span title="Travado na tabela salarial (Agrupamento × Enquadramento)" style={{
          display: "block", textAlign: "right", padding: "5px 6px", fontSize: 12,
          color: C.muted, fontWeight: 600,
        }}>
          {formatCurrency(valor)}
        </span>
      )}
      <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9, color: C.muted, cursor: "pointer", justifyContent: "flex-end" }}
        title="Usar valor diferente do estabelecido na tabela">
        <input type="checkbox" checked={!!excecao} onChange={(e) => onToggleExcecao(e.target.checked)} style={{ cursor: "pointer", width: 11, height: 11 }} />
        Exceção
      </label>
    </div>
  );
}

// Filtro de coluna estilo Excel: botão de funil no cabeçalho abre uma lista de checkboxes com
// os valores únicos da coluna (calculados a partir de todos os colaboradores do departamento,
// não apenas dos já filtrados por outras colunas, para não "sumir" opções enquanto o usuário filtra).
function FiltroColuna({ C, valores, selecionados, onChange }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!aberto) return;
    function aoClicarFora(e) { if (ref.current && !ref.current.contains(e.target)) setAberto(false); }
    document.addEventListener("mousedown", aoClicarFora);
    return () => document.removeEventListener("mousedown", aoClicarFora);
  }, [aberto]);

  if (valores.length === 0) return null;

  const filtroAtivo = !!selecionados;
  const marcados = selecionados || new Set(valores.map((v) => v.valor));

  function alternar(valor) {
    const novo = new Set(selecionados || valores.map((v) => v.valor));
    if (novo.has(valor)) novo.delete(valor); else novo.add(valor);
    onChange(novo.size === valores.length ? null : novo);
  }

  const valoresExibidos = busca ? valores.filter((v) => v.rotulo.toLowerCase().includes(busca.toLowerCase())) : valores;

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <button onClick={(e) => { e.stopPropagation(); setAberto((a) => !a); }} title="Filtrar coluna"
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, padding: 0,
          border: "none", borderRadius: 3, cursor: "pointer",
          background: filtroAtivo ? "#7C3AED" : "rgba(255,255,255,0.18)", color: "#fff",
        }}>
        <Filter size={9} />
      </button>
      {aberto && (
        <div onClick={(e) => e.stopPropagation()} style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 30, minWidth: 190, maxHeight: 280,
          overflowY: "auto", background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.3)", padding: 8, textTransform: "none", textAlign: "left",
          fontWeight: 400, letterSpacing: "normal", color: C.text, cursor: "default",
        }}>
          {valores.length > 8 && (
            <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar…"
              style={{ width: "100%", boxSizing: "border-box", marginBottom: 6, padding: "4px 6px", fontSize: 11, borderRadius: 4, border: `1px solid ${C.border}`, background: C.bg, color: C.text, fontFamily: "inherit" }} />
          )}
          <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
            <button onClick={() => onChange(null)} style={{ ...miniBtnStyle(C, false), fontSize: 10 }}>Todos</button>
            <button onClick={() => onChange(new Set())} style={{ ...miniBtnStyle(C, false), fontSize: 10 }}>Nenhum</button>
          </div>
          {valoresExibidos.map((v) => (
            <label key={String(v.valor)} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, padding: "2px 0", cursor: "pointer" }}>
              <input type="checkbox" checked={marcados.has(v.valor)} onChange={() => alternar(v.valor)} style={{ cursor: "pointer" }} />
              {v.rotulo}
            </label>
          ))}
          {valoresExibidos.length === 0 && <p style={{ fontSize: 11, color: C.muted, margin: "4px 0" }}>Nenhum valor encontrado.</p>}
        </div>
      )}
    </span>
  );
}

// ── Estilos ───────────────────────────────────────────────────────────────────

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 12px",
  cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

function btnStyle(color, secondary = false) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    background: secondary ? "transparent" : color,
    border: secondary ? `1px solid ${color}` : "none",
    color: secondary ? color : "#fff",
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "inherit", cursor: "pointer",
  };
}

function iconBtnStyle(C, danger = false) {
  return {
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 22, height: 22, borderRadius: 6, border: `1px solid ${C.border}`,
    background: "transparent", color: danger ? C.danger : C.text, cursor: "pointer", flexShrink: 0,
  };
}

// `align` deve casar com o alinhamento real do valor exibido na célula (ver tdStyle/inputs
// abaixo) — cabeçalho e dado desalinhados é o motivo do texto do cabeçalho "flutuar" fora de
// cima da coluna quando a coluna é larga (centralizado no header × esquerda/direita no dado).
function thStyle(C, reajuste = false, align = "center") {
  return {
    padding: "10px 10px", fontSize: 10.5, fontWeight: 700, color: C.navyText,
    textTransform: "uppercase", letterSpacing: "0.03em", textAlign: align,
    background: reajuste ? "#1E2B4A" : C.navy, whiteSpace: "nowrap",
    position: "sticky", top: 0, zIndex: 2,
  };
}

function tdStyle(C, strong = false) {
  return {
    padding: "6px 9px", fontSize: 12.5, color: C.text, textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
    fontWeight: strong ? 700 : 400,
  };
}

function celulaInputStyle(C) {
  return {
    width: "100%", minWidth: 110, background: "transparent", border: "1px solid transparent",
    borderRadius: 4, padding: "6px 7px", fontSize: 12.5, color: C.text, fontFamily: "inherit", outline: "none",
  };
}
